import { useEffect, useState } from "react";
import axios from "axios";
import { Clock, MapPin, Phone, Building2, Flame } from "lucide-react";
import { API } from "@/lib/site";

const fmtTime = (v) =>
  v
    ? new Intl.DateTimeFormat("en-US", {
        weekday: "short",
        hour: "numeric",
        minute: "2-digit",
        timeZone: "America/New_York",
      }).format(new Date(v))
    : null;

function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);
  if (!target) return null;
  const diff = new Date(target).getTime() - now;
  if (diff <= 0) return null;
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return d > 0 ? `${d}d ${h}h ${m}m` : `${h}h ${m}m`;
}

const ITEMS = [
  {
    icon: Clock,
    label: "Shabbat Services",
    lines: ["Fri 7:00 PM · Sat 9:30 AM", "Sun Minyan 9:00 AM (in person)"],
    testid: "info-strip-services",
  },
  {
    icon: Building2,
    label: "Office Hours",
    lines: ["Mon–Fri 9:00 AM–5:00 PM"],
    testid: "info-strip-hours",
  },
  {
    icon: MapPin,
    label: "Visit Us",
    lines: ["2815 N. Flagler Drive", "West Palm Beach, FL 33407"],
    testid: "info-strip-address",
  },
  {
    icon: Phone,
    label: "Call Us",
    lines: [],
    phone: { display: "(561) 833-0339", href: "tel:+15618330339" },
    testid: "info-strip-phone",
  },
];

function ShabbatCountdown() {
  const [d, setD] = useState(null);
  useEffect(() => {
    axios.get(`${API}/shabbat`).then((r) => setD(r.data)).catch(() => {});
  }, []);
  const holiday = d?.next_holiday;
  const useHoliday =
    holiday?.candle_lighting &&
    (!d?.candle_lighting || new Date(holiday.candle_lighting) <= new Date(d.candle_lighting));
  const target = useHoliday ? holiday.candle_lighting : d?.candle_lighting;
  const countdown = useCountdown(target);
  if (!target) return null;
  return (
    <div data-testid="info-strip-countdown" className="flex items-start gap-3">
      <Flame size={16} className="text-gold shrink-0 mt-0.5" aria-hidden="true" />
      <div>
        <p className="text-[10px] tracking-[0.28em] uppercase text-gold mb-1">
          {useHoliday ? `${holiday.title} Begins` : "Shabbat Begins"}
        </p>
        {d.hebrew_date && (
          <p className="font-cormorant italic text-gold/80 text-xs md:text-sm leading-relaxed" data-testid="info-strip-hebrew-date">
            {d.hebrew_date}
          </p>
        )}
        {countdown ? (
          <>
            <p className="text-xs md:text-sm text-foreground/90 leading-relaxed">
              in <span className="text-gold font-medium" data-testid="info-strip-countdown-value">{countdown}</span>
            </p>
            <p className="text-xs md:text-sm text-foreground/75 leading-relaxed">Candles {fmtTime(target)}</p>
          </>
        ) : (
          <p className="text-xs md:text-sm text-foreground/90 leading-relaxed" data-testid="info-strip-countdown-value">
            {useHoliday ? "Chag Sameach!" : "Shabbat Shalom!"}
          </p>
        )}
      </div>
    </div>
  );
}

export default function InfoStrip() {
  return (
    <section data-testid="info-strip" className="border-y border-gold/25 bg-inkdeep/70 backdrop-blur-sm">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-6 md:py-7 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-6">
        <ShabbatCountdown />
        {ITEMS.map((item) => (
          <div key={item.label} data-testid={item.testid} className="flex items-start gap-3">
            <item.icon size={16} className="text-gold shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <p className="text-[10px] tracking-[0.28em] uppercase text-gold mb-1">{item.label}</p>
              {item.lines.map((l) => (
                <p key={l} className="text-xs md:text-sm text-foreground/75 leading-relaxed">{l}</p>
              ))}
              {item.phone && (
                <a
                  href={item.phone.href}
                  data-testid="info-strip-phone-link"
                  className="text-xs md:text-sm text-foreground/75 hover:text-gold transition-colors"
                >
                  {item.phone.display}
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
